import React, { useState, useMemo } from 'react';
import { ChevronRightIcon, ChevronDownIcon, DocumentIcon } from '@heroicons/react/24/solid';
import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/outline';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// ##### Segmented In/Out control (same as BoardChartCategoryBar) #####
const SegmentedFlowType = ({ selectedFlowType, setSelectedFlowType }) => (
  <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
    {['In', 'Out'].map((type, index) => (
      <button
        key={type}
        onClick={() => setSelectedFlowType(type)}
        className={`
          px-3 py-1 text-xs font-medium transition-all
          ${index === 0 ? 'rounded-l-full' : 'rounded-r-full'}
          ${selectedFlowType === type
            ? type === 'In'
              ? 'text-white bg-green-400'
              : 'text-white bg-red-400'
            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
          }
        `}
      >
        {type}
      </button>
    ))}
  </div>
);

// ##### BoardDynamicTable: Hierarchical Table for Flows #####
const BoardDynamicTable = ({ flows = [], loading = false, selectedFlowCategL0 = '', selectedFlowType = 'Out', setSelectedFlowType, onFlowClick }) => {
  // ##### Transform flows to hierarchical data #####
  const data = useMemo(() => {
    // Agrupa flows por categ_l1 > categ_l2 > transação
    const l1Map = {};
    flows.forEach(flow => {
      const l1 = flow.categ_l1 || 'No category';
      const l2 = flow.categ_l2 || 'No subcategory';
      if (!l1Map[l1]) l1Map[l1] = { l1, sum: 0, count: 0, l2Map: {} };
      l1Map[l1].sum += Math.abs(+flow.std_value || 0);
      l1Map[l1].count++;
      if (!l1Map[l1].l2Map[l2]) l1Map[l1].l2Map[l2] = { l2, sum: 0, count: 0, flows: [] };
      l1Map[l1].l2Map[l2].sum += Math.abs(+flow.std_value || 0);
      l1Map[l1].l2Map[l2].count++;
      l1Map[l1].l2Map[l2].flows.push(flow);
    });
    // Ordena l1 e l2 por maior valor
    const l1Arr = Object.values(l1Map).sort((a, b) => b.sum - a.sum);
    l1Arr.forEach(l1 => {
      l1.l2Arr = Object.values(l1.l2Map).sort((a, b) => b.sum - a.sum);
      l1.l2Arr.forEach(l2 => {
        l2.flows = l2.flows.sort((a, b) => Math.abs(b.std_value) - Math.abs(a.std_value));
      });
    });
    return l1Arr;
  }, [flows]);

  // ##### Expansion state #####
  const [expandedL1, setExpandedL1] = useState({});
  const [expandedL2, setExpandedL2] = useState({});
  const toggleL1 = l1 => setExpandedL1(prev => ({ ...prev, [l1]: !prev[l1] }));
  const toggleL2 = (l1, l2) => setExpandedL2(prev => ({ ...prev, [`${l1}__${l2}`]: !prev[`${l1}__${l2}`] }));

  // ##### Render #####
  if (loading) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-3 min-h-64 relative overflow-hidden">
        <div className="mb-2">
          <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="px-2">
          <table className="min-w-full">
            <tbody>
              {[...Array(6)].map((_, idx) => (
                <tr key={idx}>
                  <td className="py-2">
                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-1"></div>
                  </td>
                  <td className="py-2">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse ml-auto"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Spinner overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
          <SpinnerLoad />
        </div>
      </div>
    );
  }
  if (!flows.length) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-3 min-h-64">
        <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-md font-semibold">Detailed info.</span>
            <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
              {selectedFlowCategL0}
            </span>
          </div>
          {setSelectedFlowType && <SegmentedFlowType selectedFlowType={selectedFlowType} setSelectedFlowType={setSelectedFlowType} />}
        </div>
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <div className="text-gray-400 mb-3">
              <DocumentIcon className="w-16 h-16 mx-auto" />
            </div>
            <div className="text-gray-500 text-sm font-medium mb-2">
              No flow data to show
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-xl p-3 min-h-64">
      <div className="mb-2 flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-md font-semibold">Detailed info.</span>
          <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
            {selectedFlowCategL0}
          </span>
        </div>
        {setSelectedFlowType && <SegmentedFlowType selectedFlowType={selectedFlowType} setSelectedFlowType={setSelectedFlowType} />}
      </div>
      <div>
        <table className="min-w-full border-separate border-spacing-y-1">
          <thead>
            <tr>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-left rounded-l">Transaction</th>
              <th className="py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right rounded-r">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.map(l1 => (
              <React.Fragment key={l1.l1}>
                {/* L1 Row */}
                <tr
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleL1(l1.l1)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleL1(l1.l1); } }}
                  className="bg-gray-100 hover:bg-gray-200 transition cursor-pointer"
                >
                  <td className="py-1.5 px-2 text-sm font-semibold text-gray-800 flex items-center rounded-l">
                    <span className="mr-2 text-gray-400 flex-shrink-0">
                      {expandedL1[l1.l1] ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                    </span>
                    {l1.l1}
                    <span className="ml-2 text-xs text-gray-400">({l1.count})</span>
                  </td>
                  <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right rounded-r">{l1.sum.toFixed(2)}</td>
                </tr>
                {expandedL1[l1.l1] && l1.l2Arr.map(l2 => (
                  <React.Fragment key={l2.l2}>
                    {/* L2 Row */}
                    <tr
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleL2(l1.l1, l2.l2)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleL2(l1.l1, l2.l2); } }}
                      className="bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                    >
                      <td className="py-1.5 px-2 text-sm font-normal text-gray-700 pl-4 flex items-center rounded-l">
                        <span className="mr-2 text-gray-400 flex-shrink-0">
                          {expandedL2[`${l1.l1}__${l2.l2}`] ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                        </span>
                        {l2.l2}
                        <span className="ml-2 text-xs text-gray-400">({l2.count})</span>
                      </td>
                      <td className="py-1.5 px-2 text-sm font-bold text-gray-800 text-right rounded-r">{l2.sum.toFixed(2)}</td>
                    </tr>
                    {expandedL2[`${l1.l1}__${l2.l2}`] && l2.flows.map(flow => (
                      <tr key={flow.id} className="bg-white hover:bg-gray-50 transition">
                        <td className="py-2 px-2 text-xs text-gray-600 pl-4 rounded-l">
                          <div className="flex items-center gap-2">
                            {onFlowClick && (
                              <button
                                onClick={() => onFlowClick(flow)}
                                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
                                title="Edit transaction"
                              >
                                <EllipsisHorizontalCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                            <div className="flex flex-col flex-1">
                              <span className="font-medium text-gray-700">{flow.desc}</span>
                              <span className="text-xs text-gray-400">{flow.date} | {flow.reference ? flow.reference : '-'}</span>
                            </div>
                          </div>
                        </td>
                        <td className={`py-2 px-2 text-sm text-right font-semibold rounded-r ${flow.flow_type === 'In' ? 'text-green-600' : 'text-red-600'}`}>{parseFloat(flow.std_value).toFixed(2)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BoardDynamicTable;